// Centralized labels for configuration options
// This ensures consistency across the application

import { LearningMethod, OutputMode, LanguageMode } from '../services/configService';

export interface ConfigOption<T> {
  value: T;
  label: string;
}

// Learning method options with labels
export const LEARNING_METHODS: ConfigOption<LearningMethod>[] = [
  { value: 'NEW', label: 'Learn new words' },
  { value: 'FOCUS', label: 'Focus on difficult words' },
  { value: 'SPACED_REPETITION', label: 'Spaced repetition' },
];

// Output mode options with labels
export const OUTPUT_MODES: ConfigOption<OutputMode>[] = [
  { value: 'DEFAULT', label: 'Default display' },
  { value: 'FLASHCARD', label: 'Flashcard mode' },
  { value: 'FLASHCARD_MOBILE_DEFAULT_DESKTOP', label: 'Flashcard on mobile, default on desktop' },
];

// Language mode options with labels
export const LANGUAGE_MODES: ConfigOption<LanguageMode>[] = [
  { value: 'JAPANESE', label: 'Japanese focus' },
  { value: 'ENGLISH', label: 'English focus' },
  { value: 'MIXED', label: 'Mixed (alternate)' },
  { value: 'KANJI', label: 'Kanji focus' },
  { value: 'VERBS', label: 'Verbs only' },
];

// Short labels for compact display (used in summary)
export const getLearningMethodShortLabel = (method: LearningMethod): string => {
  const mapping: { [key in LearningMethod]: string } = {
    NEW: 'New',
    FOCUS: 'Focus',
    SPACED_REPETITION: 'Spacing',
  };
  return mapping[method];
};

export const getOutputModeShortLabel = (mode: OutputMode): string => {
  const mapping: { [key in OutputMode]: string } = {
    DEFAULT: 'Default',
    FLASHCARD: 'Flashcard',
    FLASHCARD_MOBILE_DEFAULT_DESKTOP: 'Flashcard/Default',
  };
  return mapping[mode];
};

export const getLanguageModeShortLabel = (mode: LanguageMode): string => {
  const mapping: { [key in LanguageMode]: string } = {
    JAPANESE: 'JP',
    ENGLISH: 'EN',
    MIXED: 'Mixed',
    KANJI: 'Kanji',
    VERBS: 'Verbs',
  };
  return mapping[mode];
};

// Helper to find label by value (for dropdowns)
export const getLearningMethodLabel = (value: LearningMethod): string => {
  const method = LEARNING_METHODS.find((m) => m.value === value);
  return method?.label || value;
};

export const getOutputModeLabel = (value: OutputMode): string => {
  const mode = OUTPUT_MODES.find((m) => m.value === value);
  return mode?.label || value;
};

export const getLanguageModeLabel = (value: LanguageMode): string => {
  const languageMode = LANGUAGE_MODES.find((m) => m.value === value);
  return languageMode?.label || value;
};

